// @ts-nocheck
/**
 * Dialer Locations — unified hub with three tabs:
 *   1. Locations        — list of dialer locations (with inline Create / Edit / Delete)
 *   2. Assign Cos.   — bulk-assign companies to a location (formerly BulkLocationAddPage)
 *   3. Remove Cos.   — bulk-remove companies from a location (formerly BulkLocationDeletePage)
 *
 * Consolidates LocationListingPage + CreateLocationPage + BulkLocationAddPage +
 * BulkLocationDeletePage into one screen so users don't have to bounce between
 * four routes to manage locations and their memberships.
 *
 * Deep-link support: `?tab=locations|assign|remove` selects the initial tab
 * so the sidebar's three sub-links keep working.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2, Plus, Download, UserPlus, UserMinus, Search as SearchIcon, Users } from 'lucide-react';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, formatDateTime } from '@/utilities/helpers';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import {
  API_MANAGE_LOCATIONS_LIST,
  API_MANAGE_LOCATIONS_UPDATE,
  API_MANAGE_LOCATIONS_DELETE,
  API_MANAGE_LOCATIONS_CREATE,
  API_COMPANY_LIST,
  API_BULK_LOCATION_ADD,
  API_BULK_LOCATION_DELETE,
  API_GET_COMPANY_LOCATION,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { getApiCall } from '@/service/getApiCall';
import { postApiCall } from '@/service/postApiCall';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useModuleAcl } from '@/hooks/useModuleAcl';

const TABS: { id: 'locations' | 'assign' | 'remove'; label: string; icon: any }[] = [
  { id: 'locations', label: 'Locations', icon: Users },
  { id: 'assign', label: 'Assign Companies', icon: UserPlus },
  { id: 'remove', label: 'Remove Companies', icon: UserMinus },
];

export default function LocationList() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get('tab') as any) || 'locations';
  const [tab, setTab] = useState<'locations' | 'assign' | 'remove'>(
    ['locations', 'assign', 'remove'].includes(initialTab) ? initialTab : 'locations',
  );

  useEffect(() => {
    if (params.get('tab') !== tab) {
      const next = new URLSearchParams(params);
      next.set('tab', tab);
      setParams(next, { replace: true });
    }
  }, [tab]);

  // Shared state
  const [locations, setLocations] = useState<any[]>([]);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [locationsRes, companiesRes] = await Promise.all([
          getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
          getApiCall(API_COMPANY_LIST, {}),
        ]);
        setLocations(Array.isArray(locationsRes) ? locationsRes : []);
        setAllCompanies(Array.isArray(companiesRes) ? companiesRes : []);
      } catch (err) {
        toast.error(friendlyApiError(err));
      } finally {
        setInitialLoading(false);
      }
    })();
  }, []);

  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const { canRead } = useModuleAcl(MODULE_ACL.LOCATIONS);
  const canAdd = isSuperAdmin;
  const canEdit = isSuperAdmin;
  const canDelete = isSuperAdmin;
  const visibleTabs = isSuperAdmin ? TABS : TABS.filter((t) => t.id === 'locations');
  const activeTab = isSuperAdmin ? tab : 'locations';

  const refetchLocations = async () => {
    try {
      const data: any = await getApiCall(API_MANAGE_LOCATIONS_LIST, {});
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(friendlyApiError(err));
    }
  };

  if (!canRead) {
    return (
      <FillPage>
        <div className="my-12 text-center text-base">
          No read permission found in assigned ACL
        </div>
      </FillPage>
    );
  }

  if (initialLoading) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Dialer Locations"
        description="Manage dialer locations and their company assignments."
      />

      {/* Segmented tab strip */}
      <div className="mt-4 inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs font-medium transition-colors ' +
                (active
                  ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground')
              }
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {activeTab === 'locations' && (
          <LocationsTab
            locations={locations}
            navigate={navigate}
            canAdd={canAdd}
            canEdit={canEdit}
            canDelete={canDelete}
            refetchLocations={refetchLocations}
          />
        )}
        {activeTab === 'assign' && (
          <AssignTab
            locations={locations}
            companies={allCompanies}
          />
        )}
        {activeTab === 'remove' && (
          <RemoveTab
            locations={locations}
            companies={allCompanies}
          />
        )}
      </div>
    </FillPage>
  );
}

/* ── Locations tab ──────────────────────────────────────────────────────── */
function LocationsTab({ locations, canAdd, canEdit, canDelete, refetchLocations }: any) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editLocation, setEditLocation] = useState<any>({ id: null, name: '' });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  const [exportBusy, setExportBusy] = useState(false);

  const filtered = useMemo(
    () =>
      locations.filter((g: any) =>
        (g?.location_name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [locations, searchValue],
  );

  const toggleRow = (id: any) => {
    setSelectedLocations((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((g: any) => g.location_id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedLocations.includes(id));
    if (allSelected) {
      setSelectedLocations((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedLocations((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  async function handleCreate() {
    if (!newName.trim()) {
      toast.warning('Location name is required');
      return;
    }
    setSaving(true);
    try {
      await postApiCall(API_MANAGE_LOCATIONS_CREATE, { location_name: newName.trim() });
      toast.success('Dialer location created successfully');
      setCreateOpen(false);
      setNewName('');
      await refetchLocations();
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!editLocation.name.trim()) {
      toast.warning('Location name is required');
      return;
    }
    try {
      await postApiCall(API_MANAGE_LOCATIONS_UPDATE(editLocation.id), {
        location_name: editLocation.name.trim(),
      });
      toast.success('Dialer location updated successfully');
      setEditOpen(false);
      await refetchLocations();
    } catch (err) {
      toast.error(friendlyApiError(err));
    }
  }

  async function executeDelete() {
    setDeleting(true);
    try {
      if (bulkDeleteMode) {
        await Promise.all(
          selectedLocations.map((id) => postApiCall(API_MANAGE_LOCATIONS_DELETE, { id })),
        );
        toast.success('Dialer locations deleted successfully');
        setSelectedLocations([]);
      } else {
        await postApiCall(API_MANAGE_LOCATIONS_DELETE, { id: deleteTarget });
        toast.success('Dialer location deleted successfully');
        setSelectedLocations((prev) => prev.filter((id) => id !== deleteTarget));
      }
      await refetchLocations();
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
      setBulkDeleteMode(false);
    }
  }

  async function handleExport() {
    setExportBusy(true);
    try {
      exportToCSV({
        fileName: 'dialer-locations',
        data: filtered,
        columns: [
          { header: 'Location Id', accessor: 'location_id' },
          { header: 'Location Name', accessor: 'location_name' },
          { header: 'Created At', accessor: (row: any) => formatDateTime(row.created_at) },
        ],
      });
    } finally {
      setExportBusy(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((g: any) => selectedLocations.includes(g.location_id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedLocations.includes(row.original.location_id)}
          onCheckedChange={() => toggleRow(row.original.location_id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'location_id', header: 'Location Id' },
    { accessorKey: 'location_name', header: 'Location Name' },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditLocation({ id: row.original.location_id, name: row.original.location_name });
                setEditOpen(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
              title="Edit"
              aria-label="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original.location_id);
                setBulkDeleteMode(false);
                setDeleteOpen(true);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by location name…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
          />
        </div>
        <div className="flex items-center gap-2">
          {canDelete && selectedLocations.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setBulkDeleteMode(true);
                setDeleteOpen(true);
              }}
              className="gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete Selected ({selectedLocations.length})
            </Button>
          )}
          {locations.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={exportBusy}
              className="gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
          )}
          {canAdd && (
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Create Location
            </Button>
          )}
        </div>
      </div>

      <DataTable
        tableId="manage-locations.list:v2"
        columns={columns}
        data={filtered}
        pageSize={10}
        emptyTitle="No dialer locations found"
        disableExport
      />

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Plus className="h-4 w-4" />
              </span>
              Create Dialer Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Location name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter location name"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} className="gap-2">
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                <Pencil className="h-4 w-4" />
              </span>
              Update Dialer Location
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Location name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={editLocation.name}
              onChange={(e) => setEditLocation({ ...editLocation, name: e.target.value })}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => !next && !deleting && setDeleteOpen(next)}
        title={bulkDeleteMode ? 'Delete selected locations?' : 'Delete dialer location?'}
        description={
          bulkDeleteMode
            ? `${selectedLocations.length} location(s) will be permanently removed. This action cannot be undone.`
            : 'This location will be permanently removed. This action cannot be undone.'
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={executeDelete}
      />
    </>
  );
}

/* ── Assign Companies tab ────────────────────────────────────────────── */
function AssignTab({ locations, companies }: any) {
  const [selectedLocationId, setSelectedLocationId] = useState<any>('');
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(
    () =>
      companies.filter((c: any) =>
        (c.name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [companies, searchValue],
  );

  const toggleRow = (id: any) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((c: any) => c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  async function handleAssign() {
    setBusy(true);
    try {
      await postApiCall(API_BULK_LOCATION_ADD, {
        company_ids: selectedIds,
        location_id: selectedLocationId,
      });
      toast.success('Companies assigned to dialer location successfully');
      setSelectedIds([]);
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((c: any) => selectedIds.includes(c.id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds.includes(row.original.id)}
          onCheckedChange={() => toggleRow(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'id', header: 'Company Id' },
    { accessorKey: 'name', header: 'Company Name' },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedLocationId ? String(selectedLocationId) : undefined}
            onValueChange={(v) => setSelectedLocationId(v)}
          >
            <SelectTrigger className="h-10 w-[240px]">
              <SelectValue placeholder="Select dialer location…" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((g: any) => (
                <SelectItem key={g.location_id} value={String(g.location_id)}>
                  {g.location_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative w-full max-w-xs">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search company…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
            />
          </div>
        </div>
        <Button
          size="sm"
          disabled={selectedIds.length === 0 || !selectedLocationId || busy}
          onClick={() => setConfirmOpen(true)}
          className="gap-1.5"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Assign to Location ({selectedIds.length})
        </Button>
      </div>

      <DataTable
        tableId="bulk-location-add:v2"
        columns={columns}
        data={filtered}
        pageSize={10}
        emptyTitle="No companies found"
        disableExport
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => !next && !busy && setConfirmOpen(next)}
        title="Assign companies to location?"
        description={`${selectedIds.length} company(s) will be assigned to this dialer location.`}
        confirmLabel="Assign"
        loading={busy}
        onConfirm={handleAssign}
      />
    </>
  );
}

/* ── Remove Companies tab ────────────────────────────────────────────── */
function RemoveTab({ locations, companies }: any) {
  const [selectedLocationId, setSelectedLocationId] = useState<any>('');
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [locationCompanies, setLocationCompanies] = useState<any[]>([]);
  const [locationName, setLocationName] = useState('');
  const [applied, setApplied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filterBusy, setFilterBusy] = useState(false);

  const filtered = useMemo(
    () =>
      locationCompanies.filter((c: any) =>
        (c.name || '').toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [locationCompanies, searchValue],
  );

  async function applyFilter() {
    if (!selectedLocationId) {
      toast.warning('Please select a location first');
      return;
    }
    setFilterBusy(true);
    try {
      const locationCos: any = await getApiCall(API_GET_COMPANY_LOCATION(String(selectedLocationId)), {});
      const ids = new Set((locationCos || []).map((x: any) => Number(x.company_id)));
      const list = (companies || []).filter((c: any) => ids.has(Number(c.id)));
      setLocationCompanies(list);
      setSelectedIds([]);
      setApplied(true);
      const g = (locations || []).find((x: any) => Number(x.location_id) === Number(selectedLocationId));
      setLocationName(g?.location_name || '');
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setFilterBusy(false);
    }
  }

  function clearFilter() {
    setLocationCompanies([]);
    setSelectedIds([]);
    setSearchValue('');
    setApplied(false);
    setLocationName('');
    setSelectedLocationId('');
  }

  const toggleRow = (id: any) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleAll = () => {
    const ids = filtered.map((c: any) => c.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    if (allSelected) setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  async function handleRemove() {
    setBusy(true);
    try {
      await postApiCall(API_BULK_LOCATION_DELETE, {
        company_ids: selectedIds,
        location_id: selectedLocationId,
      });
      toast.success('Companies removed from location successfully');
      await applyFilter();
      setSelectedIds([]);
    } catch (err) {
      toast.error(friendlyApiError(err));
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={filtered.length > 0 && filtered.every((c: any) => selectedIds.includes(c.id))}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds.includes(row.original.id)}
          onCheckedChange={() => toggleRow(row.original.id)}
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'id', header: 'Company Id' },
    { accessorKey: 'name', header: 'Company Name' },
    { id: 'location_name', header: 'Location', cell: () => locationName || '—' },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedLocationId ? String(selectedLocationId) : undefined}
            onValueChange={(v) => setSelectedLocationId(v)}
          >
            <SelectTrigger className="h-10 w-[240px]">
              <SelectValue placeholder="Select dialer location…" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((g: any) => (
                <SelectItem key={g.location_id} value={String(g.location_id)}>
                  {g.location_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={applyFilter} disabled={filterBusy}>
            Load members
          </Button>
          <Button size="sm" variant="ghost" onClick={clearFilter}>
            Clear
          </Button>
          {applied && (
            <div className="relative w-full max-w-xs">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search company…"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground outline-none focus:border-ring"
              />
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="destructive"
          disabled={selectedIds.length === 0 || !selectedLocationId || busy}
          onClick={() => setConfirmOpen(true)}
          className="gap-1.5"
        >
          <UserMinus className="h-3.5 w-3.5" />
          Remove ({selectedIds.length})
        </Button>
      </div>

      {!applied ? (
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Select a dialer location and click <strong>Load members</strong> to see the assigned
          companies.
        </div>
      ) : (
        <DataTable
          tableId="bulk-location-delete:v2"
          columns={columns}
          data={filtered}
          pageSize={10}
          emptyTitle="No companies in this location"
          disableExport
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => !next && !busy && setConfirmOpen(next)}
        title="Remove companies from location?"
        description={`${selectedIds.length} company(s) will be removed from this dialer location.`}
        confirmLabel="Remove"
        tone="destructive"
        loading={busy}
        onConfirm={handleRemove}
      />
    </>
  );
}
